/* src/components/journal/SnapshotChartDisplay.js */
import React from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { METRICS } from '../../utils/analyticsUtils'; // To get formatter

// Define formatters locally or import if they become shared utils
const xAxisTickFormatter = (timestamp) => {
    // Check if timestamp is a valid number
    if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) return '';
    try {
        // Format date as "Mon DD" e.g., "Apr 11"
        return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch (e) {
        // Log error if formatting fails
        console.warn("Error formatting X-axis tick:", timestamp, e);
        return '';
    }
};

const SnapshotChartDisplay = ({ snapshotData }) => {

  // --- Log received data ---
  console.log("[SnapshotChartDisplay] Received snapshotData:", JSON.stringify(snapshotData, null, 2));
  // ---

  // Check if snapshotData or chartData is missing
  if (!snapshotData || !snapshotData.chartData) {
    console.log("[SnapshotChartDisplay] Rendering placeholder: Missing snapshotData or chartData.");
    return <div className="text-center text-gray-500 italic text-sm p-4">Chart data unavailable.</div>;
  }

  // Destructure data safely
  const {
    metricKey,
    metricName = 'Metric', // Default metric name
    chartData = [], // Default to empty array
  } = snapshotData;

  // Find the formatter for the specific metric from METRICS constant
  const metricInfo = Object.values(METRICS).find(m => m.key === metricKey);
  const valueFormatter = metricInfo?.formatter || ((value) => (value !== null && value !== undefined && Number.isFinite(value) ? value.toFixed(2) : 'N/A')); // Default formatter
  const isTimeMetric = metricKey === METRICS.TOTAL_PLAY_TIME.key;
  const isSessionCountMetric = metricKey === METRICS.NUM_SESSIONS.key;


  // Prepare data for Recharts: Convert ISO strings back to timestamps, ensure numeric values
   const validDataPoints = chartData
    .map((d, index) => { // Added index for more specific logging
        let timestamp = null;
        // --- Date Parsing Logic ---
        try {
            // Check if date is already a number (timestamp) - less likely but possible
            if (typeof d.date === 'number' && Number.isFinite(d.date)) {
                timestamp = d.date;
            }
            // Check if date is a string (ISO format expected from Firestore)
            else if (typeof d.date === 'string') {
                 const parsedDate = new Date(d.date);
                 // Validate the parsed date
                 if (!isNaN(parsedDate.getTime())) {
                     timestamp = parsedDate.getTime();
                 } else {
                     // Log warning if the string couldn't be parsed into a valid date
                     console.warn(`[SnapshotChartDisplay] Invalid date string format in chartData[${index}]:`, d.date);
                 }
            }
            // Log warning for other unexpected types if 'date' exists but isn't string/number
            else if (d.date) {
                 console.warn(`[SnapshotChartDisplay] Unexpected date type in chartData[${index}]:`, typeof d.date, d.date);
            }
            // If d.date is null or undefined, timestamp remains null
        } catch (parseError) {
             // Catch any unexpected errors during date parsing
             console.warn(`[SnapshotChartDisplay] Error parsing date in chartData[${index}]:`, d.date, parseError);
        }
        // --- End Date Parsing Logic ---

        // Return the processed point, ensuring values are numbers or null
        return {
            ...d, // Keep original properties if needed elsewhere
            timestamp: timestamp, // Use the parsed timestamp (or null if parsing failed)
            // Ensure current/previous are numbers or explicitly null
            current: (typeof d.current === 'number' && Number.isFinite(d.current)) ? d.current : null,
            previous: (typeof d.previous === 'number' && Number.isFinite(d.previous)) ? d.previous : null,
        };
    })
    // Filter out any points where timestamp parsing failed
    .filter(d => d.timestamp !== null);

  // --- Log processed data ---
  console.log(`[SnapshotChartDisplay] Processed ${validDataPoints.length} valid data points out of ${chartData.length} initial points.`);
  if (validDataPoints.length > 0) {
      // Log a sample of the data that will actually be used by the chart
      console.log("  Processed Data Sample (first 3):", JSON.stringify(validDataPoints.slice(0, 3), null, 2));
  } else if (chartData.length > 0) {
      // Log if all points were filtered out, likely due to date issues
      console.warn("[SnapshotChartDisplay] No valid data points remained after processing dates.");
  }
  // ---

  // Check if there's enough valid data to render a meaningful line chart
  // Requires at least 2 points for a line, and at least one non-null value for display
  const hasSignificantData = validDataPoints.length >= 2 && validDataPoints.some(d => d.current !== null || d.previous !== null);

  if (!hasSignificantData) {
    console.log(`[SnapshotChartDisplay] Rendering placeholder: Not enough significant data points (valid points: ${validDataPoints.length}).`);
    // Render a placeholder message if data is insufficient
    return <div className="text-center text-gray-500 italic text-sm p-4">Not enough data to display chart.</div>;
  }

  // Calculate domains for the axes
  // X-axis (Time): Use explicit min/max to ensure correct scale
  const timestamps = validDataPoints.map(p => p.timestamp);
  const minTimestamp = Math.min(...timestamps);
  const maxTimestamp = Math.max(...timestamps);
  // Handle case where min/max might be the same if only one day has data (add padding)
  const xAxisDomain = (Number.isFinite(minTimestamp) && Number.isFinite(maxTimestamp))
                      ? (minTimestamp === maxTimestamp ? [minTimestamp - (60*60*1000*12), maxTimestamp + (60*60*1000*12)] : [minTimestamp, maxTimestamp]) // Add 12hr padding if single point
                      : ['auto', 'auto']; // Fallback if min/max calculation failed

  // Y-axis (Value): Let Recharts determine the upper bound, start at 0 unless all data is null
   const hasAnyFiniteValue = validDataPoints.some(p =>
        (p.current !== null && Number.isFinite(p.current)) ||
        (p.previous !== null && Number.isFinite(p.previous))
   );
   // Use [0, 1] as default ONLY if ALL values are null/invalid, otherwise let Recharts calculate upper bound [0, 'auto']
   const yAxisDomain = hasAnyFiniteValue ? [0, 'auto'] : [0, 1];
   // Use specific formatter for Y-axis ticks only if it's a time metric
   const yAxisTickFormatter = isTimeMetric ? valueFormatter : undefined;


  // Render the static chart using Recharts
  return (
    // Container with a fixed height suitable for the journal entry
    <div className="my-3 h-48 w-full"> {/* Adjusted height */}
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={validDataPoints} // Feed the processed data points
          // Adjust margins for the smaller chart size
          margin={{ top: 5, right: 10, left: isTimeMetric ? 0 : -20, bottom: 0 }}
        >
          {/* Grid lines */}
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          {/* X Axis Configuration */}
          <XAxis
            allowDuplicatedCategory={false} // Prevent duplicate labels if timestamps somehow collide
            dataKey="timestamp" // Key containing the numeric timestamp
            scale="time" // Indicate it's a time-based scale
            type="number" // Timestamps are numbers
            domain={xAxisDomain} // Use the calculated min/max domain
            tickFormatter={xAxisTickFormatter} // Format timestamps into readable dates
            tick={{ fontSize: 9 }} // Use smaller font for ticks in journal
            name="Date"
            interval="preserveStartEnd" // Ensure start and end labels are shown
            // Consider dynamically setting tickCount based on period length if needed
            // tickCount={Math.min(7, validDataPoints.length)} // Example limit
          />
          {/* Y Axis Configuration */}
          <YAxis
            tick={{ fontSize: 9 }} // Smaller font for Y ticks
            tickFormatter={yAxisTickFormatter} // Apply specific formatter if metric is time-based
            domain={yAxisDomain} // Use calculated Y domain
            width={isTimeMetric ? 50 : 35} // Adjust width based on potential label length
            name={metricName}
            // Allow decimal ticks for most metrics, but not counts or time
            allowDecimals={!isSessionCountMetric && !isTimeMetric}
          />
          {/* Tooltip Configuration (shows data on hover) */}
          <Tooltip
                contentStyle={{ fontSize: '10px', padding: '3px 5px', borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                itemStyle={{ padding: '1px 0' }}
                // Format the value displayed using the metric's specific formatter
                formatter={(value, name) => [valueFormatter(value), name === 'current' ? 'Current' : 'Previous']}
                // Format the label (timestamp) back into a readable date string
                labelFormatter={(label) => {
                    try { return new Date(label).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
                    catch (e) { return 'Date'; }
                }}
                // Style the line that follows the cursor
                cursor={{ stroke: '#a0aec0', strokeWidth: 1, strokeDasharray: '2 2' }}
          />
           {/* Legend Configuration */}
          <Legend wrapperStyle={{ fontSize: "10px", paddingTop: '5px', paddingBottom: '0px' }} />

          {/* Line Definitions - Disable animations for static display */}
          <Line isAnimationActive={false} type="monotone" dataKey="previous" stroke="#a0aec0" strokeWidth={1.5} name="Previous" dot={false} connectNulls={false} />
          <Line isAnimationActive={false} type="monotone" dataKey="current" stroke="#4f46e5" strokeWidth={1.5} name="Current" dot={false} connectNulls={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default SnapshotChartDisplay;